import React, { FC } from 'react';
import { Typography, Input } from 'antd';
import DateInput from '~/components/inputs/DateInput';
import { merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import produce from 'immer';

import { Form, NationalitySelect } from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { NameFn } from '~/utils/buildNamePath';
import Section from '~/layouts/Section ';
import { useValidator } from '~/controllers/validation';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { useGetApplication } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { isReadOnly } from '~/utils/isReadOnly';
import { getStagePayload } from '~/utils/getStagePayload';
import ReviewItemCheckbox from '~/pages/ksa/review/components/ReviewItemCheckbox';
import { KsaBeneficialOwner } from '~/types/ksa/KsaBeneficialOwner';
import NationalityField from '~/components/fields/NationalityField';
import IdTypeField from '~/components/fields/IdTypeField';
import { ExclamationCircleFilled } from '@ant-design/icons';
import colors from '~/constants/colors';
import WithPep from '~/components/WithPep';
import { Stage } from '~/types/Application';
import { isEnglish } from '~/utils/isEnglish';
import { useGetlocalizedValue } from '~/hooks/useGetlocalizedValue';
import { detectFieldIssue } from '~/utils/detectFieldIssue';

interface Props {
  n: NameFn;
  beneficialOwnerData: KsaBeneficialOwner;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const BeneficialOwnerReviewForm: FC<Props> = (props) => {
  const { onFinish, initialValues, n, beneficialOwnerData: beneficialOwner } = props;
  const { t } = useTranslation('ksaReview');

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(getFormState, undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);
  const { localizedValue: localizedValueNationality, isReady } = useGetlocalizedValue(
    beneficialOwner.personalData.nationality,
    'nationality'
  );
  const idType = beneficialOwner.personalDocument.idType?.toUpperCase();

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: undefined,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Container className="IndividualAdd">
        <Form
          form={form}
          name={formName}
          onFinish={onFinish}
          onValuesChange={(_: any, newValues: any) => {
            optionalFieldsController.debouncedUpdate(newValues);
          }}
          initialValues={initialValues}
        >
          <Form.Item name={n(['uid'])} hidden initialValue={beneficialOwner?.uid} />
          <Section>
            <Typography.Title level={4}>{t('Personal data').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <ReviewItemCheckbox label={t('First name')} name={n(['personalData', 'firstName'])}>
                {beneficialOwner.personalData.firstName}
              </ReviewItemCheckbox>
              <Form.Item
                label={t('First name')}
                name={n(['personalData', 'englishFirstName'])}
                initialValue={
                  isEnglish(beneficialOwner.personalData.firstName)
                    ? beneficialOwner.personalData.firstName
                    : undefined
                }
              >
                <Input disabled={!detectFieldIssue(beneficialOwner.personalData.firstName)} />
              </Form.Item>
              <ReviewItemCheckbox label={t('Last name')} name={n(['personalData', 'lastName'])}>
                {beneficialOwner.personalData.lastName}
              </ReviewItemCheckbox>
              <Form.Item
                label={t('Last name')}
                name={n(['personalData', 'englishLastName'])}
                initialValue={
                  isEnglish(beneficialOwner.personalData.lastName)
                    ? beneficialOwner.personalData.lastName
                    : undefined
                }
              >
                <Input disabled={!detectFieldIssue(beneficialOwner.personalData.lastName)} />
              </Form.Item>
              <ReviewItemCheckbox
                label={t('Nationality')}
                name={n(['personalData', 'nationality'])}
              >
                <NationalityField value={beneficialOwner.personalData.nationality} />
              </ReviewItemCheckbox>
              {isReady && (
                <Form.Item
                  label={t('Nationality')}
                  name={n(['personalData', 'englishNationality'])}
                  initialValue={
                    isEnglish(localizedValueNationality)
                      ? beneficialOwner.personalData.nationality
                      : undefined
                  }
                >
                  {/* Disabled when Thiqah nationality is already a clean English value */}
                  <NationalitySelect disabled={!detectFieldIssue(localizedValueNationality)} />
                </Form.Item>
              )}
              <ReviewItemCheckbox
                label={t('Date of birth')}
                name={n(['personalData', 'dateOfBirth'])}
              >
                {beneficialOwner.personalData.dateOfBirth}
              </ReviewItemCheckbox>
              {/* Date of birth Agent Updates field added (Risk Team requirement): Thiqah sometimes returns it blank */}
              <Form.Item
                labelText={t('Date of birth – Agent Updates')}
                name={n(['personalData', 'agentDateOfBirth'])}
                initialValue={beneficialOwner.personalData.dateOfBirth || undefined}
              >
                <DateInput disabled={!detectFieldIssue(beneficialOwner.personalData.dateOfBirth)} />
              </Form.Item>
            </InputsGroupLayout>
            <ReviewItemCheckbox
              name={n(['personalData', 'isPep'])}
              label={
                <WithPep>{t('Beneficial owner is a Politically Exposed Person (PEP)')}</WithPep>
              }
            >
              {beneficialOwner.personalData.isPep ? (
                <>
                  <ExclamationCircleFilled style={{ color: colors.warningColor }} /> {t('Yes')}
                </>
              ) : (
                t('No')
              )}
            </ReviewItemCheckbox>
          </Section>

          <Section>
            <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <ReviewItemCheckbox
                label={t('Passport number')}
                name={n(['passport', 'documentNumber'])}
              >
                {beneficialOwner.passport.documentNumber}
              </ReviewItemCheckbox>
              <ReviewItemCheckbox
                label={t('Passport issue date')}
                name={n(['passport', 'issueDate'])}
              >
                {beneficialOwner.passport.issueDate}
              </ReviewItemCheckbox>
              <ReviewItemCheckbox
                label={t('Passport expiry date')}
                name={n(['passport', 'expiryDate'])}
              >
                {beneficialOwner.passport.expiryDate}
              </ReviewItemCheckbox>
            </InputsGroupLayout>
          </Section>

          <Section>
            <Typography.Title level={4}>{t('Personal document').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <ReviewItemCheckbox label={t('ID type')} name={n(['personalDocument', 'idType'])}>
                {idType ? <IdTypeField value={idType} /> : undefined}
              </ReviewItemCheckbox>
              {/* ID Type Agent Updates field added (Risk Team requirement): Thiqah can return it in Arabic or blank */}
              <Form.Item
                labelText={t('ID Type')}
                name={n(['personalDocument', 'agentIdType'])}
                initialValue={beneficialOwner.personalDocument.idType || undefined}
              >
                <Input disabled={!detectFieldIssue(beneficialOwner.personalDocument.idType)} placeholder={t('Enter ID type')} />
              </Form.Item>
              <ReviewItemCheckbox label={t('ID number')} name={n(['personalDocument', 'idNumber'])}>
                {beneficialOwner.personalDocument.idNumber}
              </ReviewItemCheckbox>
              {/* ID Number is required for compliance; editable only when Thiqah returns it missing or in Arabic */}
              <Form.Item
                labelText={t('ID Number – Agent Updates')}
                name={n(['personalDocument', 'agentIdNumber'])}
                initialValue={beneficialOwner.personalDocument.idNumber || undefined}
              >
                <Input disabled={!detectFieldIssue(beneficialOwner.personalDocument.idNumber)} placeholder={t('Enter ID number')} />
              </Form.Item>
            </InputsGroupLayout>
          </Section>

          <section>
            <Typography.Title level={4}>{t('Ownership share').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <ReviewItemCheckbox label={t('Shares, %')} name={n(['ownershipShare', 'percent'])}>
                {beneficialOwner.ownershipShare?.percent}
              </ReviewItemCheckbox>
              {/* Shares %, count, share type, and entity relationship type are Risk Team fields;
                  Shares % is disabled when Thiqah already returned a valid value */}
              <Form.Item
                labelText={t('Shares % – Agent Updates')}
                name={n(['ownershipShare', 'agentPercent'])}
                initialValue={beneficialOwner.ownershipShare?.percent || undefined}
              >
                <Input type="number" disabled={!detectFieldIssue(beneficialOwner.ownershipShare?.percent?.toString())} placeholder={t('Enter shares %')} />
              </Form.Item>
              <Form.Item
                labelText={t('Shares count – Agent Updates')}
                name={n(['ownershipShare', 'agentCount'])}
                initialValue={undefined}
              >
                <Input type="number" placeholder={t('Enter shares count')} />
              </Form.Item>
              <Form.Item
                labelText={t('Share Type – Agent Updates')}
                name={n(['ownershipShare', 'agentShareType'])}
                initialValue={undefined}
              >
                <Input placeholder={t('Enter share type')} />
              </Form.Item>
              {/* Entity Relationship Type is not in the applicant form; always a manual agent entry */}
              <Form.Item
                labelText={t('Entity Relationship Type – Agent Updates')}
                name={n(['agentEntityRelationshipType'])}
                initialValue={undefined}
              >
                <Input placeholder={t('Enter entity relationship type')} />
              </Form.Item>
            </InputsGroupLayout>
          </section>
        </Form>
      </Container>
    </FormItemContextProvider>
  );
};

export const formName = 'beneficialOwners';
export const getBeneficialOwnersReviewFormItemsNamePath = (index: number) => [
  'reviewShareholders',
  formName,
  index,
];

const Container = styled.div`
  .IndividualAdd-SharesInput {
    width: 100%;
  }
`;

export default BeneficialOwnerReviewForm;
